/**
 * Figma 387:5493 (TagRoundFill) — 8개 상태 태그 정의
 *
 * 사용처:
 * - 홈 환자관리 패널 (8개 모두)
 * - 처방전 관리 탭 (visiting/pos 제외 6개)
 *
 * 컴포넌트: src/core/components/dashboard/StatusTag/StatusTag.jsx
 */

import { ORDER_STATUS } from './orderStatus';

/**
 * Figma 8개 상태 태그 정의
 * 각 키는 컴포넌트 prop으로 직접 전달 가능
 */
export const STATUS_TAG_DEFS = {
  pending: {
    label: '승인대기',
    bg: '#fff7eb',
    border: '#ffe0b2',
    color: '#f79103',
    weight: 600, // SemiBold
  },
  waiting: {
    label: '대기요청',
    bg: '#fbf5ff',
    border: '#edccff',
    color: '#c254fe',
    weight: 600,
  },
  pickedup: {
    label: '수령완료',
    bg: '#fafafa',
    border: '#f4f4f5',
    color: '#d4d4d8',
    weight: 500, // Medium
  },
  visiting: {
    label: '방문중',
    bg: '#f4f4f5',
    border: '#cacace',
    color: '#3f3f46',
    weight: 500, // Medium
  },
  pos: {
    label: 'POS',
    bg: '#f4f4f5',
    border: '#cacace',
    color: '#3f3f46',
    weight: 500, // Medium
  },
  approved: {
    label: '승인됨',
    bg: '#f4f4f5',
    border: '#cacace',
    color: '#3f3f46',
    weight: 500, // Medium
  },
  finishedMaking: {
    label: '조제완료',
    bg: '#eef2ff',
    border: '#b2c4ff',
    color: '#007aff',
    weight: 600,
  },
  preparing: {
    label: '조제중',
    bg: '#f8eeff',
    border: '#dfb2ff',
    color: '#9f1aff',
    weight: 600,
  },
  rejected: {
    label: '조제불가',
    bg: '#fff1f0',
    border: '#ffb7b2',
    color: '#e33e35',
    weight: 600,
  },
  paid: {
    label: '결제완료',
    bg: '#f3fbf5',
    border: '#c4edcd',
    color: '#34c759',
    weight: 600,
  },
};

/**
 * 처방전 status (orders 테이블) → 태그 키 매핑
 * 처방전 관리 탭에서 사용
 */
export const RX_STATUS_TO_TAG = {
  [ORDER_STATUS.PENDING]: 'pending',
  [ORDER_STATUS.WAITING]: 'waiting',
  [ORDER_STATUS.APPROVED]: 'approved', // 승인됨 (visiting/pos와 동일 회색 톤)
  [ORDER_STATUS.PAID]: 'paid',
  [ORDER_STATUS.PREPARING]: 'preparing',
  [ORDER_STATUS.COMPLETED]: 'finishedMaking',
  [ORDER_STATUS.PICKED_UP]: 'pickedup',
  [ORDER_STATUS.REJECTED]: 'rejected',
  [ORDER_STATUS.CANCELLED]: 'pickedup', // 취소는 회색 처리
};

/**
 * 홈 환자 status (rx_status + 추가 환자 상태) → 태그 키 매핑
 * 홈 환자관리 패널에서 사용 (8개 모두 표시 가능)
 */
export const HOME_STATUS_TO_TAG = {
  ...RX_STATUS_TO_TAG,
  visiting: 'visiting',
  pos: 'pos',
};

/**
 * 처방전 관리 탭에서 숨길 상태들 (홈 전용 상태)
 */
export const HIDDEN_STATES_IN_RX = ['visiting', 'pos'];

/**
 * 홈 환자 패널 row → 태그 키 결정 (서버 응답 구조 기반)
 *
 * 서버 (`/pharm-dashboard/patients`) 응답은 3가지 출처를 합쳐서 보냄:
 *  - Source A: orders        → rx_status = order.status (pending/paid/...)
 *  - Source B: pos_prescriptions → rx_status = pos.status, pos_serial 채워짐
 *  - Source C: pharm_visits  → rx_status = "none", visit_channel='app'
 *
 * 우선순위:
 *  1. pos_serial(또는 walk-in) — POS 출처면 무조건 'pos'
 *  2. rx_status === 'none' — 방문만 한 환자 → 'visiting'
 *  3. 그 외는 RX_STATUS_TO_TAG 매핑 → 매핑 없으면 null (태그 미표시)
 */
export const getHomeTagState = (patient) => {
  if (!patient) return null;
  if (patient.pos_serial || patient.visit_channel === 'walk-in') {
    return 'pos';
  }
  if (patient.rx_status === 'none') {
    return 'visiting';
  }
  return RX_STATUS_TO_TAG[patient.rx_status] ?? null;
};
